/**
 * FormField Component
 *
 * Generic wrapper for form fields providing consistent layout for
 * labels, help text, and error messages across all forms.
 *
 * FS-015 RWP-F: Adds aria-describedby, aria-invalid, and error icon.
 */

import React from "react";
import { renderHelpText } from "../utils/helpTextRenderer";

export interface FormFieldProps {
  /** Field label text */
  label: string;
  /** HTML id for the associated input (used in htmlFor) */
  htmlFor?: string;
  /** Optional help text displayed below the field */
  helpText?: string;
  /** Optional error message */
  error?: string;
  /** Whether field is required (shows red asterisk) */
  required?: boolean;
  /** Child input/select element */
  children: React.ReactNode;
}

/**
 * FormField - Consistent wrapper for form labels, inputs, help text, and errors.
 *
 * Clones child input to inject aria-describedby and aria-invalid when an
 * error or help text is present, linking them for screen readers.
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  helpText,
  error,
  required,
  children,
}) => {
  const fieldId = htmlFor ?? label.toLowerCase().replace(/\s+/g, "-");
  const helpId = helpText ? `${fieldId}-help` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;

  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  // Clone the first React element child to inject ARIA attributes
  const enhancedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const ariaProps: Record<string, string | undefined> = {};
    if (describedBy) ariaProps["aria-describedby"] = describedBy;
    if (error) ariaProps["aria-invalid"] = "true";
    return React.cloneElement(child as React.ReactElement, ariaProps);
  });

  return (
    <div>
      <label htmlFor={htmlFor} className="form-label">
        {label}
        {required && (
          <span className="form-required-indicator" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      {enhancedChildren}
      {helpText && (
        <p id={helpId} className="form-help-text">
          {renderHelpText(helpText)}
        </p>
      )}
      {error && (
        <p id={errorId} className="form-field-error-message" role="alert">
          <span className="form-field-error-icon" aria-hidden="true">
            ⚠
          </span>
          {error}
        </p>
      )}
    </div>
  );
};
